import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, CheckCircle, ArrowRight, Phone } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getUTM } from "@/utils/utm";
import { pushEvent } from "@/utils/dataLayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadMagnetPage({ guideName, title, subtitle, bullets, contents, audience, testimonial }) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "" });

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setLoading(true);
    try {
      const utm = getUTM();
      await axios.post(`${API}/leads`, { ...form, guide_name: guideName, ...utm });
      pushEvent("lead_magnet_download", { guide_name: guideName });
      setSubmitted(true);
      toast.success("Guide is ready for download!");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid={`guide-${guideName}`} className="min-h-screen bg-[#0A0A0A]">
      {/* Hero */}
      <section className="pt-28 pb-16 sm:pt-36 bg-[#141414]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left — Info */}
            <div>
              <div className="industrial-line mb-6" />
              <span className="text-[#FF5722] text-xs font-bold uppercase tracking-wider">Free Guide</span>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1] mt-3 mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                {title}
              </h1>
              <p className="text-base text-[#A3A3A3] mb-6">{subtitle}</p>
              <ul className="space-y-3 mb-8">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-[#A3A3A3]">
                    <CheckCircle size={16} className="text-[#FF5722] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              {audience && (
                <div className="bg-[#0A0A0A] border border-[#2E2E2E] p-4 mb-6">
                  <p className="text-xs text-[#666] uppercase tracking-wider mb-2">Who this guide is for</p>
                  <p className="text-sm text-[#A3A3A3]">{audience}</p>
                </div>
              )}
            </div>

            {/* Right — Form */}
            <div className="bg-[#0A0A0A] border border-[#2E2E2E] p-6 sm:p-8">
              {submitted ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
                  <div className="w-14 h-14 bg-[#FF5722]/10 flex items-center justify-center mx-auto mb-4">
                    <Download size={24} className="text-[#FF5722]" />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>Your Guide is Ready!</h3>
                  <p className="text-sm text-[#A3A3A3] mb-6">We&apos;ve also sent a copy to your email. Check your inbox.</p>
                  <p className="text-xs text-[#666] mb-6">While you&apos;re here, need a quote for your facility?</p>
                  <Link to="/quote" className="inline-flex items-center gap-2 bg-[#FF5722] text-white px-6 py-3 font-semibold text-sm hover:bg-[#E64A19] transition-colors">
                    Request a Free Quote <ArrowRight size={16} />
                  </Link>
                </motion.div>
              ) : (
                <>
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: "'Clash Display', sans-serif" }}>Download Free Guide</h3>
                  <p className="text-xs text-[#A3A3A3] mb-6">Fill in your details to access the guide instantly.</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label className="text-sm text-[#A3A3A3] mb-1.5 block">Full Name *</Label>
                      <Input name="name" data-testid="lead-name" value={form.name} onChange={handleChange} required placeholder="John Smith" className="bg-[#1F1F1F] border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none h-11" />
                    </div>
                    <div>
                      <Label className="text-sm text-[#A3A3A3] mb-1.5 block">Work Email *</Label>
                      <Input name="email" type="email" data-testid="lead-email" value={form.email} onChange={handleChange} required placeholder="john@company.com.au" className="bg-[#1F1F1F] border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none h-11" />
                    </div>
                    <div>
                      <Label className="text-sm text-[#A3A3A3] mb-1.5 block">Company</Label>
                      <Input name="company" data-testid="lead-company" value={form.company} onChange={handleChange} placeholder="Your Company Pty Ltd" className="bg-[#1F1F1F] border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none h-11" />
                    </div>
                    <div>
                      <Label className="text-sm text-[#A3A3A3] mb-1.5 block">Phone (optional)</Label>
                      <Input name="phone" type="tel" data-testid="lead-phone" value={form.phone} onChange={handleChange} placeholder="+61..." className="bg-[#1F1F1F] border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none h-11" />
                    </div>
                    <button type="submit" data-testid="lead-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-[#FF5722] text-white py-3 font-semibold text-sm hover:bg-[#E64A19] transition-colors disabled:opacity-50">
                      {loading ? "Processing..." : <><Download size={16} /> Download Free Guide</>}
                    </button>
                    <p className="text-[10px] text-[#666] text-center">We respect your privacy. Unsubscribe anytime.</p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contents Preview */}
      {contents && (
        <section className="py-16 bg-[#0A0A0A]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold mb-6" style={{ fontFamily: "'Clash Display', sans-serif" }}>What&apos;s Inside</h2>
            <div className="space-y-3">
              {contents.map((item, i) => (
                <div key={item} className="flex items-start gap-3 text-sm">
                  <span className="text-[#FF5722] font-bold w-6 shrink-0">{i + 1}.</span>
                  <span className="text-[#A3A3A3]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 bg-[#141414] border-t border-[#2E2E2E]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-[#A3A3A3] text-sm mb-4">Prefer to talk to someone directly?</p>
          <a href="tel:+61413136160" className="inline-flex items-center gap-2 text-[#FF5722] font-semibold text-sm hover:text-[#E64A19]">
            <Phone size={16} /> +61 413 136 160
          </a>
        </div>
      </section>
    </div>
  );
}
